// Panel administrativo de Gastos: todos los gastos/pagos registrados por los cajeros, sin abrir cada cierre.
// Lee los mismos registros que el detalle del cierre (GET /api/gastos); el estado se deriva de su cierre.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { listGastosAdmin } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { nombreTurno } from '../../utils/turnos';
import { fechaCorta } from '../finanzas/estados';
import { ComprobanteLink } from '../finanzas/Comprobante';
import PeriodoFiltro, { usePeriodo } from '../cierres/PeriodoFiltro';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';

const ESTADOS = ['Turno abierto', 'Pendiente de revisar', 'En corrección', 'Revisada', 'Pendiente de catálogo'];
const claseEstado = { 'Turno abierto': 'parcial', 'Pendiente de revisar': 'hoy', 'En corrección': 'parcial', Revisada: 'completado' };

export default function GastosPanel() {
  const { sucursalId, todas } = useSucursalActiva();
  const per = usePeriodo('mes');
  const { desde, hasta } = per;
  const [filtros, setFiltros] = useState({ cajero: '', tipo: '', beneficiario: '', estado: '' });
  const [buscar, setBuscar] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [cierre, setCierre] = useState(null);
  useEffect(() => { const t = setTimeout(() => setQ(buscar.trim()), 300); return () => clearTimeout(t); }, [buscar]);

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    setCargando(true);
    try { setData(await listGastosAdmin({ desde, hasta, sucursalId, ...filtros, q })); }
    catch (e) { Swal.fire('Error', e.message || 'No se pudieron cargar los gastos.', 'error'); }
    finally { setCargando(false); }
  }, [sucursalId, desde, hasta, filtros, q]);
  useEffect(() => { cargar(); }, [cargar]);
  const abierto = useMemo(() => (detalle && data ? data.gastos.find((g) => g.id === detalle) : null), [detalle, data]);
  const set = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));
  const r = data?.resumen;

  return (
    <div className="prov-shell">
      <header className="prov-header"><div><h1>Gastos</h1>
        <p style={{ margin: 0, color: 'var(--ink-muted)' }}>Gastos y pagos registrados por los cajeros en Caja{todas ? ' · todas las sucursales' : ''}. Cada uno sigue ligado a su cierre.</p></div></header>

      <section className="fx-card cc-filtros" aria-label="Filtros de gastos">
        <PeriodoFiltro p={per} />
        <div className="prov-filtros cc-grupo" role="group" aria-label="Filtros">
          <span className="filtro-titulo">Filtros</span>
          <input type="search" placeholder="Buscar tipo, beneficiario o descripción" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar" />
          {data?.opciones.cajeros.length > 1 && <label>Cajero<select value={filtros.cajero} onChange={set('cajero')}><option value="">Todos</option>{data.opciones.cajeros.map((x) => <option key={x}>{x}</option>)}</select></label>}
          {data?.opciones.tipos.length > 1 && <label>Tipo<select value={filtros.tipo} onChange={set('tipo')}><option value="">Todos</option>{data.opciones.tipos.map((x) => <option key={x}>{x}</option>)}</select></label>}
          {data?.opciones.beneficiarios.length > 1 && <label>Beneficiario<select value={filtros.beneficiario} onChange={set('beneficiario')}><option value="">Todos</option>{data.opciones.beneficiarios.map((x) => <option key={x}>{x}</option>)}</select></label>}
          <label>Estado<select value={filtros.estado} onChange={set('estado')}><option value="">Todos</option>{ESTADOS.map((x) => <option key={x}>{x}</option>)}</select></label>
        </div>
      </section>

      {r && <section className="cd-resumen" aria-label="Resumen de gastos">
        <dl className="cd-resumen-grid cp-resumen">
          <div><dt>Total de gastos</dt><dd>{toMoney(r.total)}</dd></div>
          <div><dt>Registros</dt><dd>{r.cantidad}</dd></div>
          <div><dt>Pendientes de revisar</dt><dd>{r.pendientesRevisar}</dd></div>
          <div><dt>Pendientes de catálogo</dt><dd>{r.pendientesCatalogo}</dd></div>
        </dl>
      </section>}

      <div className="fx-card">
        {cargando && !data ? <p className="fx-empty">Cargando…</p> : data?.gastos.length ? (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Fecha</th><th>Sucursal</th><th>Cajero</th><th>Tipo</th><th>Beneficiario</th><th>Descripción</th><th className="num">Monto</th><th>Estado</th><th /></tr></thead>
            <tbody>{data.gastos.map((g) => (
              <tr key={g.id}>
                <td data-label="Fecha">{fechaCorta(g.fecha)}</td>
                <td data-label="Sucursal">{g.sucursal}</td>
                <td data-label="Cajero">{g.cajero || '—'}</td>
                <td data-label="Tipo">{g.tipo || '—'}</td>
                <td data-label="Beneficiario">{g.beneficiario || '—'}</td>
                <td data-label="Descripción">{g.descripcion || '—'}</td>
                <td data-label="Monto" className="num"><b>{toMoney(g.monto)}</b></td>
                <td data-label="Estado"><span className={`fx-badge fx-badge--${claseEstado[g.estado] || 'hoy'}`}>{g.estado}</span>
                  {(g.tipoPendiente || g.beneficiarioPendiente) && <span className="fx-badge" style={{ marginLeft: 4 }} title="Tipo o beneficiario sin asociar al catálogo">Pendiente de catálogo</span>}</td>
                <td><button className="fx-btn" onClick={() => setDetalle(g.id)}>Ver detalle</button></td>
              </tr>))}</tbody>
          </table>
        ) : <p className="fx-empty">No hay gastos con estos filtros.</p>}
      </div>

      {abierto && <div className="fx-backdrop" role="presentation" onMouseDown={() => setDetalle(null)}>
        <section className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Detalle de gasto" onMouseDown={(e) => e.stopPropagation()}>
          <header><div><h2>{abierto.tipo || 'Gasto'}</h2><p>{abierto.sucursal} · {fechaCorta(abierto.fecha)} · Cajero: {abierto.cajero || '—'}</p></div>
            <button type="button" aria-label="Cerrar" onClick={() => setDetalle(null)}>×</button></header>
          <dl className="cp-datos">
            <div><dt>Tipo</dt><dd>{abierto.tipo || '—'}{abierto.tipoPendiente && <> <span className="fx-badge">Pendiente de catálogo</span></>}</dd></div>
            <div><dt>Beneficiario</dt><dd>{abierto.beneficiario || '—'}{abierto.beneficiarioPendiente && <> <span className="fx-badge">Pendiente de catálogo</span></>}</dd></div>
            <div><dt>Forma de pago</dt><dd>{abierto.formaPago}</dd></div>
            <div><dt>Estado</dt><dd><span className={`fx-badge fx-badge--${claseEstado[abierto.estado] || 'hoy'}`}>{abierto.estado}</span></dd></div>
            <div><dt>Descripción</dt><dd>{[abierto.descripcion, abierto.referencia && `Ref. ${abierto.referencia}`].filter(Boolean).join(' · ') || '—'}</dd></div>
            {abierto.observacion && <div><dt>Observación</dt><dd>{abierto.observacion}</dd></div>}
            {abierto.aplicaciones.length > 0 && <div><dt>Facturas</dt><dd>{abierto.aplicaciones.map((a) => `${a.documento || 'Fact.'}: ${toMoney(a.saldoAnterior)} → ${toMoney(a.saldoNuevo)}`).join(' · ')}</dd></div>}
            <div><dt>Comprobante</dt><dd>{abierto.fileUrl ? <ComprobanteLink url={abierto.fileUrl} /> : 'Sin comprobante'}</dd></div>
          </dl>
          <div className="cd-reg-total"><span>Total gasto</span><b>{toMoney(abierto.monto)}</b></div>
          <p className="fx-empty" style={{ padding: '8px 0 0' }}>El catálogo de tipo y beneficiario se revisa desde el detalle del cierre.</p>
          <div className="fx-actions">
            {abierto.cierreId ? <button type="button" className="fx-btn fx-btn--primary" onClick={() => setCierre(abierto.cierreId)}>
              Ver cierre relacionado ({fechaCorta(abierto.fecha)} · {nombreTurno(abierto.turnoId)})</button>
              : <small className="fx-empty">Registrado en un turno que aún no se ha cerrado.</small>}
          </div>
        </section>
      </div>}
      {cierre && <CierreDetalleModal cierreId={cierre} onClose={() => setCierre(null)} onChanged={cargar} />}
    </div>
  );
}
